'use client';

import { __spreadArray, __assign } from '../_virtual/_tslib.js';
import React__default, { useState, useRef, useMemo, useEffect } from 'react';
import { IconChevronDown, IconSearch } from '../icons/index.js';
import IconCheck from '../icons/IconCheck.js';
import s from '../Input/input.module.css.js';
import { transformToLatin, filterInt } from '../Utils/helpers.js';
import StyledSelect, { RightAlignedIcon, LeftAlignedAbsIcon } from './styled.js';
import { keyCode } from '../SearchBarUnstable/constants.js';

var DEFAULT_ID = -1;
var DEFAULT_LIMIT_SHOW_RESULT = 4;
var DEFAULT_LIMIT_CHARACTER = 26;
function Select(props) {
    /**
     * Data for rendering
     */
    var dropdownWidth = props.dropdownWidth, presetOptions = props.options, _a = props.initValue, initValue = _a === void 0 ? -1 : _a, allOptionText = props.allOptionText, _b = props.searchNotFound, searchNotFound = _b === void 0 ? 'No Result' : _b, onSelected = props.onSelected, title = props.title, _c = props.bordered, bordered = _c === void 0 ? false : _c, _d = props.fullWidth, fullWidth = _d === void 0 ? false : _d, _e = props.fullDropdownWidth, fullDropdownWidth = _e === void 0 ? false : _e, _f = props.hasSearchBox, hasSearchBox = _f === void 0 ? true : _f, _g = props.alignDropdown, alignDropdown = _g === void 0 ? 'left' : _g, hasError = props.hasError, _h = props.disabled, disabled = _h === void 0 ? false : _h, mode = props.mode, maxNumberOfSelectionMessage = props.maxNumberOfSelectionMessage, maxNumberOfSelection = props.maxNumberOfSelection, onFocus = props.onFocus, onBlur = props.onBlur, label = props.label, isRequired = props.isRequired, _j = props.isShowChecked, isShowChecked = _j === void 0 ? false : _j, _k = props.showCheckedMaxShowCount, showCheckedMaxShowCount = _k === void 0 ? DEFAULT_LIMIT_SHOW_RESULT : _k, _l = props.isDisplayCount, isDisplayCount = _l === void 0 ? false : _l, _m = props.limitCharacterShow, limitCharacterShow = _m === void 0 ? DEFAULT_LIMIT_CHARACTER : _m, _o = props.isShowAlertInside, isShowAlertInside = _o === void 0 ? false : _o, _p = props.isShowTag, isShowTag = _p === void 0 ? true : _p, _q = props.showTagMinusLength, showTagMinusLength = _q === void 0 ? 6 : _q, onKeyDown = props.onKeyDown, onExpandToggle = props.onExpandToggle;
    /**
     * Data for rendering
     */
    var _r = useState(false), expand = _r[0], setExpand = _r[1];
    var _s = useState(''), query = _s[0], setQuery = _s[1];
    var isMultiple = mode === 'multiple'; // TODO: will do the tag mode later
    var initialSingleValue = isMultiple ? -1 : initValue;
    // Process initial value to make sure it works for the multiple case
    var initialMultipleValue = (function () {
        if (isMultiple) {
            if (Array.isArray(initValue)) {
                return initValue;
            }
            // Convert the single value into an array
            return [initValue];
        }
        return [];
    })();
    var _t = useState(initialSingleValue), value = _t[0], setValue = _t[1];
    var _u = useState(initialMultipleValue), multipleValue = _u[0], setMultipleValue = _u[1];
    var _v = useState(false), isValueChanged = _v[0], setIsValueChanged = _v[1];
    var hasReachedMaxSelection = maxNumberOfSelection && multipleValue.length >= maxNumberOfSelection;
    // Focus event option list
    var _w = useState(-1), selectedIndex = _w[0], setSelectedIndex = _w[1];
    var selectedOptionItemRef = useRef(null);
    // END Focus event option list
    var inputRef = useRef(null);
    var selectContainerRef = useRef(null);
    var optionContainerRef = useRef(null);
    var options = useMemo(function () {
        if (allOptionText && !isShowChecked) {
            return __spreadArray([
                {
                    id: DEFAULT_ID,
                    name: allOptionText
                }
            ], presetOptions, true);
        }
        return __spreadArray([], presetOptions, true);
    }, [presetOptions]);
    var normalizedSearchableOptions = useMemo(function () {
        return options.map(function (option) {
            return __assign(__assign({}, option), { searchableName: transformToLatin(option.name.toLowerCase()).replace(/[^\w\s]/gi, ' ') });
        });
    }, [options]);
    // let focusOptionIndex = -1
    // const totalOptions = options.length
    var expandClass = expand ? 'expand' : '';
    var borderClass = bordered ? 'bordered' : '';
    var disabledClass = disabled ? 'disabled' : '';
    var fullWidthClass = fullWidth ? 'full-width' : '';
    var hasErrorClass = hasError ? 'error' : '';
    var isShowItem = false;
    var isFocusOnSelect = false;
    if (isMultiple && isValueChanged) {
        expandClass = 'expand';
    }
    var handleUpdateSelection = function (id, isKeyUp) {
        var _a;
        if (isKeyUp === void 0) { isKeyUp = false; }
        // some cases ID is not really a number
        var parsedId = filterInt(id);
        var canSetValue = true;
        if (isShowChecked && hasReachedMaxSelection) {
            // TODO: scroll to alert message
            (_a = optionContainerRef.current) === null || _a === void 0 ? void 0 : _a.scroll(0, 0);
            canSetValue = false;
        }
        // Update the internal state for rendering
        if (isMultiple) {
            if (canSetValue) {
                setMultipleValue(__spreadArray(__spreadArray([], multipleValue, true), [parsedId], false));
                setIsValueChanged(true);
            }
        }
        else {
            if (isNaN(parsedId)) {
                setValue(id);
            }
            else {
                setValue(parsedId);
            }
        }
        // Output this value
        if (onSelected) {
            if (isMultiple) {
                if (canSetValue) {
                    onSelected(__spreadArray(__spreadArray([], multipleValue, true), [parsedId], false));
                }
            }
            else {
                onSelected(id, isKeyUp);
            }
        }
        if (isShowChecked) {
            // This prevent Select div lost focus and can't close
            setTimeout(function () {
                var _a;
                (_a = selectContainerRef === null || selectContainerRef === void 0 ? void 0 : selectContainerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 50);
        }
    };
    /**
     * un-select option by click it again
     * @param id id of option
     * @param isKeyUp is key up = false
     */
    var handleUncheckedSelection = function (id, isKeyUp) {
        var parsedId = filterInt(id);
        // Update the internal state for rendering
        // This function is only work when isShowChecked. Because when isShowChecked = true it will have this function bind
        if (isMultiple && isShowChecked) {
            var filteredMultipleValue = multipleValue.filter(function (item) { return item !== parsedId; });
            setMultipleValue(filteredMultipleValue);
            setIsValueChanged(true);
            onSelected && onSelected(filteredMultipleValue);
            // This prevent Select div lost focus and can't close
            setTimeout(function () {
                var _a;
                (_a = selectContainerRef === null || selectContainerRef === void 0 ? void 0 : selectContainerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 50);
        }
    };
    // Clicking out side would close the dropdown
    var isClickOutside = function (event, ref) {
        if (ref.current) {
            if (ref.current.contains(event.target))
                return false;
        }
        return true;
    };
    var handleClickOnScreen = function (e) {
        if (isClickOutside(e, selectContainerRef) && expand) {
            setExpand(false);
        }
        // why have this condition:
        // in multiple select mode, expand will return false right after first click.
        // So checking expand is no longer correct
        if (isClickOutside(e, selectContainerRef) && isShowChecked) {
            isFocusOnSelect = false;
            // handle close
            setExpand(false);
            // reset change to hide expand section
            setIsValueChanged(false);
        }
    };
    // ESC would blur the current active element
    var handleClickEsc = function (e) {
        if (e.keyCode === 27) {
            setExpand(false);
            // reset change to hide expand section
            setIsValueChanged(false);
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        }
    };
    useEffect(function () {
        document.addEventListener('keydown', handleClickEsc);
        return function () {
            document.removeEventListener('keydown', handleClickEsc);
        };
    }, []);
    // Show/hide the dropdown when users click on the select
    useEffect(function () {
        if (expand) {
            setTimeout(function () {
                var _a;
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                isShowItem = true;
            }, 200);
            onExpandToggle && onExpandToggle(expand);
        }
        else {
            isShowItem = false;
            if (query !== '') {
                setQuery('');
            }
        }
        document.addEventListener('mousedown', handleClickOnScreen);
        return function () {
            document.removeEventListener('mousedown', handleClickOnScreen);
        };
    }, [expand]);
    // Everytime the select values change, we close the dropdown
    useEffect(function () {
        if (expand) {
            setExpand(false);
        }
    }, [value, JSON.stringify(multipleValue)]);
    // If the values from the props change, we force update it to the internal state
    useEffect(function () {
        if (isMultiple) {
            setMultipleValue(initValue);
        }
        else {
            setValue(initValue);
        }
    }, [initValue]);
    var selectedOption = options.find(function (item) { return item.id === value; });
    var selectedLabel = selectedOption ? selectedOption.name : allOptionText;
    var allOptions = normalizedSearchableOptions.filter(function (option) {
        var normalizedQuery = transformToLatin(query.toLowerCase());
        var isContain = true;
        if (query !== '') {
            isContain = option.searchableName.indexOf(normalizedQuery) > -1;
            if (!isContain) {
                // Check for name
                var optionName = transformToLatin(option.name.toLowerCase());
                isContain = optionName.indexOf(normalizedQuery) > -1;
            }
        }
        return isContain;
    });
    var filteredOptions = allOptions;
    var filteredSelectedOptions = allOptions;
    if (isMultiple) {
        filteredOptions = filteredOptions.filter(function (item) { return !multipleValue.includes(item.id); });
    }
    if (isShowChecked) {
        var sortedList_1 = [];
        multipleValue.map(function (itemId) {
            var filteredValue = allOptions.filter(function (item) { return item.id === itemId; });
            if (filteredValue.length > 0) {
                sortedList_1.push(filteredValue[0]);
            }
        });
        filteredSelectedOptions = sortedList_1;
    }
    var getOptionNameInfo = function (options, id, index) {
        var _a, _b, _c, _d;
        var optionInfo = {
            name: ((_a = options.find(function (item) { return item.id === id; })) === null || _a === void 0 ? void 0 : _a.name) || '',
            isShow: true
        };
        var concatStrValue = '';
        var countValueIndex = -1;
        var isHavePlus = false;
        // Only get those option that index smaller than showCheckedMaxShowCount (default: 4)
        // concat all text in those option to get final text display
        var valueListFiltered = multipleValue.filter(function (_, valueIndex) { return valueIndex < showCheckedMaxShowCount; });
        valueListFiltered.map(function (valueItem, _) {
            var optionValueListFiltered = options.filter(function (item) { return item.id === valueItem; });
            if (optionValueListFiltered.length > 0) {
                concatStrValue += optionValueListFiltered[0].name;
                countValueIndex += 1;
            }
        });
        // Check if
        // - text length is large than limit and
        // - index is larger than count and
        // - index is not 0 (not first item)
        // Then make option name as '+1' or '...'
        if (concatStrValue.length > limitCharacterShow && index >= countValueIndex && index > 0) {
            optionInfo.name = isDisplayCount ? "+".concat(multipleValue.length - countValueIndex) : '...';
            if (isDisplayCount) {
                isHavePlus = true;
            }
            if (!isShowTag) {
                optionInfo.name = ', ' + optionInfo.name;
            }
        }
        // Check if
        // index is 0 and
        //  text length is larger than limit
        if (index === 0 && optionInfo.name !== undefined && ((_b = optionInfo.name) === null || _b === void 0 ? void 0 : _b.length) > limitCharacterShow) {
            var limitCharacter = limitCharacterShow;
            if (multipleValue.length > 1) {
                limitCharacter = limitCharacterShow - showTagMinusLength;
            }
            optionInfo.name = ((_c = optionInfo.name) === null || _c === void 0 ? void 0 : _c.substring(0, limitCharacter)) + '...';
        }
        if (concatStrValue.length > limitCharacterShow && index > countValueIndex) {
            optionInfo.isShow = false;
            // allow last option item '(+ number)' to display
            if (optionInfo !== undefined &&
                (optionInfo === null || optionInfo === void 0 ? void 0 : optionInfo.name) !== undefined &&
                ((_d = optionInfo === null || optionInfo === void 0 ? void 0 : optionInfo.name) === null || _d === void 0 ? void 0 : _d.indexOf('+')) > -1 &&
                !isHavePlus) {
                optionInfo.isShow = true;
            }
        }
        return optionInfo;
    };
    var removeOneMultipleOption = function (id) {
        var newValues = multipleValue.filter(function (item) { return item !== id; });
        setMultipleValue(newValues);
        setIsValueChanged(true);
        // Output the new values
        if (onSelected) {
            onSelected(newValues);
        }
    };
    var handleOnClickArrowDown = function () {
        var isShow = false;
        if (isShowItem) {
            isShow = false;
        }
        else if (!isShowItem && !isFocusOnSelect) {
            isShow = true;
        }
        if (!isShow) {
            // This is trick to force Select hide
            setIsValueChanged(false);
            setTimeout(function () {
                var _a;
                setExpand(isShow);
                (_a = selectContainerRef === null || selectContainerRef === void 0 ? void 0 : selectContainerRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                onExpandToggle && onExpandToggle(false);
            }, 50);
        }
    };
    /**
     * Handlers
     */
    // Navigate the dropdown list by pressing up/down
    var handleInputKeydown = function (e) {
        var _a, _b;
        onKeyDown && onKeyDown(e.key);
        // select option by arrow key
        var defaultMaxItemCount = allOptions.length;
        var maxIndex = defaultMaxItemCount - 1;
        if (e.key === keyCode.ArrowUp) {
            e.preventDefault();
            (_a = selectContainerRef === null || selectContainerRef === void 0 ? void 0 : selectContainerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            // move up 1 select index
            var nextIndex = selectedIndex - 1;
            if (nextIndex < 0) {
                // is over current then reset to 0
                nextIndex = maxIndex;
            }
            setSelectedIndex(nextIndex);
        }
        else if (e.key === keyCode.ArrowDown) {
            e.preventDefault();
            (_b = selectContainerRef === null || selectContainerRef === void 0 ? void 0 : selectContainerRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            // move down
            var nextIndex = selectedIndex + 1;
            if (nextIndex > maxIndex) {
                // is over current then reset to 0
                nextIndex = 0;
            }
            setSelectedIndex(nextIndex);
        }
        else if (e.key === keyCode.Tab) {
            if (expand) {
                setExpand(false);
                // reset change to hide expand section
                setIsValueChanged(false);
            }
        }
        else if (e.key === keyCode.Enter) {
            e.preventDefault();
            // Check item is selected or not
            if (selectedIndex >= filteredSelectedOptions.length) {
                // use un-select list
                var fixedIndex = selectedIndex - filteredSelectedOptions.length;
                if (hasReachedMaxSelection) {
                    return;
                }
                handleUpdateSelection(filteredOptions[fixedIndex].id);
            }
            else {
                handleUncheckedSelection(filteredSelectedOptions[selectedIndex].id);
            }
        }
    };
    useEffect(function () {
        if (selectedOptionItemRef !== null &&
            (selectedOptionItemRef === null || selectedOptionItemRef === void 0 ? void 0 : selectedOptionItemRef.current) !== undefined &&
            (selectedOptionItemRef === null || selectedOptionItemRef === void 0 ? void 0 : selectedOptionItemRef.current) !== null) {
            selectedOptionItemRef.current.focus();
            if (inputRef !== null && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) !== null) {
                inputRef.current.focus();
            }
        }
    }, [selectedIndex, selectedOptionItemRef]);
    /* Render */
    // Render multiple values
    var renderMultipleValues = function () {
        if (multipleValue.length === 0) {
            return (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("div", { className: 'active-item', onClick: function () {
                        if (isShowChecked && expand) {
                            handleOnClickArrowDown();
                        }
                    } },
                    React__default.createElement("span", { className: "active-text ".concat(!selectedOption || (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.id) === DEFAULT_ID ? 'text-default' : '') }, selectedLabel)),
                isShowChecked ? (React__default.createElement(RightAlignedIcon, { onClick: handleOnClickArrowDown, width: 11 },
                    React__default.createElement(IconChevronDown, null))) : (React__default.createElement(React__default.Fragment, null))));
        }
        var optionNodes = multipleValue.map(function (id, index) {
            var _a;
            var optionName = ((_a = options.find(function (item) { return item.id === id; })) === null || _a === void 0 ? void 0 : _a.name) || '';
            var isShowOption = true;
            if (isShowChecked) {
                var optionInfo = getOptionNameInfo(options, id, index);
                optionName = optionInfo.name || '';
                isShowOption = optionInfo.isShow || false;
            }
            if (isShowChecked && index === showCheckedMaxShowCount) {
                optionName = isDisplayCount ? "+".concat(multipleValue.length - showCheckedMaxShowCount) : '...';
            }
            if (isShowChecked && index > showCheckedMaxShowCount) {
                isShowOption = false;
            }
            return (React__default.createElement("span", { className: "multiple-option-item ".concat(isShowOption ? '' : 'hide'), key: "option-item-".concat(id) },
                optionName,
                ' ',
                !isShowChecked ? (React__default.createElement("i", { onClick: function (event) {
                        event.stopPropagation();
                        // Blur, so that it would be able to focus again.
                        // WHY: We're only showing the dropdown when the input focuses.
                        // When users click on the delete button, it already focuses, therefore it will never focus
                        // again if we don't blur it
                        if (document.activeElement instanceof HTMLElement) {
                            document.activeElement.blur();
                        }
                        removeOneMultipleOption(id);
                    } }, "\u00D7")) : (React__default.createElement(React__default.Fragment, null))));
        });
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("div", { className: 'multiple-select-selected-values', onClick: function () {
                    if (isShowChecked && expand) {
                        handleOnClickArrowDown();
                    }
                } }, optionNodes),
            React__default.createElement(RightAlignedIcon, { onClick: handleOnClickArrowDown, width: 11 },
                React__default.createElement(IconChevronDown, null))));
    };
    // Render the select
    return (React__default.createElement(StyledSelect, null,
        title && React__default.createElement("div", { className: 'title' }, title),
        label ? (React__default.createElement("div", { className: s.labelWrapper },
            isRequired ? React__default.createElement("span", { className: 'text-danger' }, "*") : null,
            " ",
            React__default.createElement("label", null, label))) : null,
        React__default.createElement("div", { className: "select-container ".concat(expandClass, " ").concat(borderClass, " ").concat(disabledClass, " ").concat(fullWidthClass, " ").concat(hasErrorClass, " ").concat(isShowChecked ? 'show-checked' : '', " ").concat(isShowTag ? '' : 'not-show-tag'), tabIndex: 0, onFocus: function (event) {
                if (isShowChecked) {
                    isFocusOnSelect = true;
                    setExpand(true);
                }
                else {
                    if (!hasReachedMaxSelection && !expand) {
                        setExpand(true);
                    }
                }
                onFocus && onFocus(event);
            }, onBlur: function (event) {
                if (isShowChecked) {
                    isFocusOnSelect = false;
                }
                onBlur && onBlur(event);
            }, onKeyDown: handleInputKeydown, ref: selectContainerRef },
            isMultiple ? (renderMultipleValues()) : (React__default.createElement("div", { className: 'active-item', onMouseDown: function (e) {
                    e.preventDefault();
                    setExpand(!expand);
                } },
                React__default.createElement("span", { className: "active-text ".concat(!selectedOption || (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.id) === DEFAULT_ID ? 'text-default' : '') }, selectedLabel),
                React__default.createElement(RightAlignedIcon, { width: 11 },
                    React__default.createElement(IconChevronDown, null)))),
            React__default.createElement("div", { className: "select-dropdown select-dropdown-align-".concat(alignDropdown), style: {
                    width: fullDropdownWidth ? '100%' : dropdownWidth || '335px'
                } },
                React__default.createElement("div", { className: "select-search ".concat(!hasSearchBox ? 'select-search-no-searchBox' : '') }, hasSearchBox ? (React__default.createElement("div", { className: 'input-item' },
                    React__default.createElement(LeftAlignedAbsIcon, { width: 16 },
                        React__default.createElement(IconSearch, null)),
                    React__default.createElement("input", { type: 'text', className: 'input', ref: inputRef, value: query, onChange: function (e) { return setQuery(e.target.value); } }),
                    React__default.createElement("svg", { width: 14, height: 14, className: "filter-close ".concat(query ? 'active' : ''), viewBox: '0 0 50 50', onClick: function () { return setQuery(''); } },
                        React__default.createElement("path", { d: 'M 14.40625 13 L 13 14.40625 L 23.625 25 L 13 35.59375 L 14.40625 37 L 25.0625 26.40625 L 35.6875 37 L 37.09375 35.59375 L 26.46875 25 L 37.09375 14.40625 L 35.6875 13 L 25.0625 23.59375 Z' })))) : (
                // This div is not necessary in UI but a tricky way to trigger handleUpDownKey when tabbing in case of No Search Box (hasSearchBox = false)
                React__default.createElement("div", { ref: inputRef, tabIndex: 0 }))),
                React__default.createElement("div", { className: 'select-options', ref: optionContainerRef, tabIndex: -1 },
                    isShowChecked ? (filteredSelectedOptions.map(function (item, index) {
                        var isSelectedByArrow = selectedIndex === index;
                        return (React__default.createElement("div", { key: item.id, className: "select-item ".concat('active' , " ").concat(isSelectedByArrow ? 'focusing' : ''), title: item.name, onClick: function (e) {
                                handleUncheckedSelection(item.id);
                            }, tabIndex: -1, "data-option-id": item.id, ref: isSelectedByArrow ? selectedOptionItemRef : null },
                            item.svgImg || React__default.createElement("div", { className: 'item-text' }, item.name),
                            (React__default.createElement(RightAlignedIcon, { width: 14, primary: true },
                                React__default.createElement(IconCheck, null)))));
                    })) : (React__default.createElement(React__default.Fragment, null)),
                    isShowChecked && filteredSelectedOptions.length > 0 ? (React__default.createElement("div", { className: 'divided-section' })) : (React__default.createElement(React__default.Fragment, null)),
                    isShowAlertInside && maxNumberOfSelectionMessage && hasReachedMaxSelection && (React__default.createElement("span", { className: 'max-number-of-selection-message small mt-1' }, maxNumberOfSelectionMessage)),
                    isShowChecked
                        ? filteredOptions.map(function (item, index) {
                            var isSelectedByArrow = selectedIndex === filteredSelectedOptions.length + index;
                            return (React__default.createElement("div", { key: item.id, className: "select-item ".concat(isSelectedByArrow ? 'focusing' : ''), title: item.name, onClick: function () {
                                    handleUpdateSelection(item.id);
                                }, tabIndex: -1, "data-option-id": item.id, ref: isSelectedByArrow ? selectedOptionItemRef : null },
                                item.svgImg || React__default.createElement("div", { className: 'item-text' }, item.name),
                                React__default.createElement("span", { className: 'unchecked' })));
                        })
                        : filteredOptions.map(function (_a) {
                            var id = _a.id, name = _a.name, svgImg = _a.svgImg;
                            var isSelected = id === value;
                            return (React__default.createElement("div", { key: id, className: "select-item ".concat(isSelected ? 'active' : ''), title: name, onClick: function (e) {
                                    if (isSelected) {
                                        e.preventDefault();
                                        return;
                                    }
                                    handleUpdateSelection(id);
                                }, tabIndex: -1, "data-option-id": id },
                                svgImg || React__default.createElement("div", { className: 'item-text' }, name),
                                isSelected && (React__default.createElement(RightAlignedIcon, { width: 14, primary: true },
                                    React__default.createElement(IconCheck, null)))));
                        }),
                    filteredOptions.length === 0 && React__default.createElement("div", { className: 'select-item read-only' }, searchNotFound)))),
        !isShowAlertInside && maxNumberOfSelectionMessage && hasReachedMaxSelection && (React__default.createElement("span", { className: 'max-number-of-selection-message small text-muted mt-1' }, maxNumberOfSelectionMessage))));
}

export { Select as default };
//# sourceMappingURL=index.js.map
